"use client";

import Image from "next/image";
import type { PipelineCandidate } from "@/lib/api/types";
import { Badge } from "@/components/ui/badge";
import { Icon } from "@/components/ui/icon";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  NameCell as CandidateNameCell,
  fromPipelineCandidate,
} from "@/features/candidate";
import {
  useConnectorLogo,
  useJobConnectorLogo,
} from "@/features/jobs/hooks/use-connector-logo";
import { usePipelineJob } from "@/features/jobs/context/pipeline-job-context";
import { useJobAtsLink } from "@/features/ats-sync/hooks/use-job-ats-link";

function AtsProvenance({ candidate }: { candidate: PipelineCandidate }) {
  const { job } = usePipelineJob();
  const { syncDirection } = useJobAtsLink(job.id);
  const candidateLogo = useConnectorLogo(candidate.ats_connector_id);
  const jobLogo = useJobConnectorLogo(job.id);
  const atsLogo = candidateLogo ?? jobLogo;
  const fromLabel =
    syncDirection === "pull_only"
      ? `Imported from ${candidate.ats_connector_name ?? "an ATS"}`
      : `Synced from ${candidate.ats_connector_name ?? "an ATS"}`;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex">
          {atsLogo ? (
            <Image
              src={atsLogo}
              alt=""
              width={22}
              height={22}
              className="size-5.5 shrink-0 rounded-sm object-cover"
              unoptimized
            />
          ) : (
            <Badge variant="linear" size="icon-sm" style={{
              "--gradient-color": "#34d399",
            } as React.CSSProperties}>
              <Icon name="IconPuzzle" size={12} className="text-emerald-600 dark:text-emerald-400" />
            </Badge>
          )}
        </span>
      </TooltipTrigger>
      <TooltipContent>{fromLabel}</TooltipContent>
    </Tooltip>
  );
}

export function NameCell({ candidate }: { candidate: PipelineCandidate }) {
  return (
    <CandidateNameCell
      row={fromPipelineCandidate(candidate)}
      trailing={
        candidate.origin === "ats" ? <AtsProvenance candidate={candidate} /> : null
      }
    />
  );
}
